import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Confetti from "react-confetti";
import wordWheelQuestIcon from "../../assets/app-icons/word-wheel-quest-app-icon.png";
import { WORD_WHEEL_QUEST_GOOGLE_PLAY_URL } from "../../config/nativeAppsVisibility";
import ScreenshotPlayPanel from "../../prototype/mobile-word-wheel/components/ScreenshotPlayPanel";
import { buildWheelTiles, lettersForWheel } from "../../prototype/mobile-word-wheel/components/LetterWheel";
import {
    buildCellWordNumbers,
    buildWordToNumberMap,
    normalizeWord,
} from "../../prototype/mobile-word-wheel/lib/gridReveal";
import { usePagesTranslation } from "../locales";
import LandingPhoneStoreCta from "./LandingPhoneStoreCta";

const INITIAL_MS = 800;
const CLUE_MS = 1400;
const LETTER_MS = 560;
const HOLD_WORD_MS = 420;
const REVEAL_MS = 800;
const CONFETTI_MS = 7200;
const COMPLETE_HOLD_MS = 2200;
/**
 * Layout on a larger canvas, then CSS-scale into the phone.
 * Shrinks clamp() font floors with the rest of the UI so tiles do not overlap.
 */
const LAYOUT_SCALE = 0.78;

/** Compact crossword used when the newest published Word Wheel has not loaded yet. */
export const DEMO_WORD_WHEEL_PUZZLE = {
    id: "landing-hero-ww",
    wordsInUse: "STAR\nART\nTAR",
    gridSize: 8,
    filledCoordinates: [
        {
            word: "STAR",
            direction: "horizontal",
            positions: [
                { row: 3, col: 2, value: "S" },
                { row: 3, col: 3, value: "T" },
                { row: 3, col: 4, value: "A" },
                { row: 3, col: 5, value: "R" },
            ],
        },
        {
            word: "ART",
            direction: "vertical",
            positions: [
                { row: 3, col: 4, value: "A" },
                { row: 4, col: 4, value: "R" },
                { row: 5, col: 4, value: "T" },
            ],
        },
        {
            word: "TAR",
            direction: "vertical",
            positions: [
                { row: 3, col: 3, value: "T" },
                { row: 4, col: 3, value: "A" },
                { row: 5, col: 3, value: "R" },
            ],
        },
    ],
    displayClue: [
        { word: "STAR", definition: "A bright point of light in the night sky" },
        { word: "ART", definition: "Creative work such as painting or music" },
        { word: "TAR", definition: "Dark sticky substance used on roads" },
    ],
};

export const DEMO_WORD_WHEEL_FOUND = [];

function parseWords(wordsInUse) {
    if (!wordsInUse) return [];
    return wordsInUse.split("\n").map((w) => w.trim()).filter(Boolean);
}

function findWheelIndicesForWord(tiles, word) {
    const letters = normalizeWord(word).split("");
    if (letters.length === 0) return null;
    const used = new Set();
    const indices = [];
    for (const ch of letters) {
        let found = -1;
        for (let i = 0; i < tiles.length; i += 1) {
            if (used.has(i)) continue;
            if ((tiles[i].letter || "").toUpperCase() === ch) {
                found = i;
                break;
            }
        }
        if (found < 0) return null;
        used.add(found);
        indices.push(found);
    }
    return indices;
}

function wordsFromPuzzle(puzzle) {
    const fromUse = parseWords(puzzle?.wordsInUse).map(normalizeWord);
    const coords = Array.isArray(puzzle?.filledCoordinates) ? puzzle.filledCoordinates : [];
    const seen = new Set();
    const words = [];
    [...fromUse, ...coords.map((c) => normalizeWord(c?.word || c?.text))].forEach((word) => {
        if (!word || seen.has(word)) return;
        seen.add(word);
        words.push(word);
    });
    return words;
}

function pickLandingDemoWords(puzzle) {
    const words = wordsFromPuzzle(puzzle);
    const tiles = buildWheelTiles(
        lettersForWheel(words, puzzle?.lettersGrid, puzzle?.details),
        puzzle?.id || "screenshot-wheel",
    );
    const wordToNumber = buildWordToNumberMap(
        puzzle?.filledCoordinates,
        buildCellWordNumbers(puzzle?.filledCoordinates),
    );
    const spellable = words.filter((word) => findWheelIndicesForWord(tiles, word));
    spellable.sort((a, b) => (wordToNumber.get(a) || 999) - (wordToNumber.get(b) || 999));
    return spellable;
}

/** Word Wheel Quest play: start empty, show clue, drag the wheel, reveal on the grid. */
export default function LandingWordWheelPlayPreview({
    puzzle = DEMO_WORD_WHEEL_PUZZLE,
    foundWords: _foundWords = DEMO_WORD_WHEEL_FOUND,
    background = "deep_sea",
    active = true,
    onBusyChange,
    onPlayComplete,
}) {
    const { t } = usePagesTranslation();
    const demoWords = useMemo(() => pickLandingDemoWords(puzzle), [puzzle]);
    const demoKey = demoWords.join(",");
    const wheelTiles = useMemo(() => {
        const words = wordsFromPuzzle(puzzle);
        return buildWheelTiles(
            lettersForWheel(words, puzzle?.lettersGrid, puzzle?.details),
            puzzle?.id || "screenshot-wheel",
        );
    }, [puzzle]);

    const [foundWords, setFoundWords] = useState([]);
    const [clueWord, setClueWord] = useState(null);
    const [clueMode, setClueMode] = useState(false);
    const [dragIndices, setDragIndices] = useState([]);
    const [confettiActive, setConfettiActive] = useState(false);
    const [frameSize, setFrameSize] = useState({ w: 320, h: 480 });

    const rootRef = useRef(null);
    const onBusyChangeRef = useRef(onBusyChange);
    onBusyChangeRef.current = onBusyChange;
    const onPlayCompleteRef = useRef(onPlayComplete);
    onPlayCompleteRef.current = onPlayComplete;

    const resetPlay = () => {
        setFoundWords([]);
        setClueWord(null);
        setClueMode(false);
        setDragIndices([]);
        setConfettiActive(false);
    };

    useLayoutEffect(() => {
        const el = rootRef.current;
        if (!el) return undefined;
        const apply = () => {
            setFrameSize({
                w: Math.max(1, el.clientWidth),
                h: Math.max(1, el.clientHeight),
            });
        };
        apply();
        const observer = new ResizeObserver(apply);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!active) return undefined;
        onBusyChangeRef.current?.(true);
        return () => onBusyChangeRef.current?.(false);
    }, [active]);

    useEffect(() => {
        if (!active || !puzzle || demoWords.length === 0) {
            resetPlay();
            return undefined;
        }

        let cancelled = false;
        const timers = [];
        const wait = (ms) =>
            new Promise((resolve) => {
                const id = window.setTimeout(resolve, ms);
                timers.push(id);
            });

        const run = async () => {
            resetPlay();
            await wait(INITIAL_MS);
            if (cancelled) return;

            for (let w = 0; w < demoWords.length; w += 1) {
                const word = demoWords[w];
                const indices = findWheelIndicesForWord(wheelTiles, word);
                if (!indices || indices.length === 0) continue;

                setClueWord(word);
                setClueMode(true);
                setDragIndices([]);
                await wait(CLUE_MS);
                if (cancelled) return;

                setClueMode(false);
                for (let i = 0; i < indices.length; i += 1) {
                    setDragIndices(indices.slice(0, i + 1));
                    await wait(LETTER_MS);
                    if (cancelled) return;
                }
                await wait(HOLD_WORD_MS);
                if (cancelled) return;

                setFoundWords((prev) => (prev.includes(word) ? prev : [...prev, word]));
                setDragIndices([]);
                await wait(REVEAL_MS);
                if (cancelled) return;
            }

            setClueWord(null);
            setClueMode(false);
            setConfettiActive(true);
            await wait(CONFETTI_MS + COMPLETE_HOLD_MS);
            if (!cancelled) {
                setConfettiActive(false);
                onBusyChangeRef.current?.(false);
                onPlayCompleteRef.current?.();
            }
        };

        run();
        return () => {
            cancelled = true;
            timers.forEach((id) => window.clearTimeout(id));
        };
    }, [active, puzzle, demoKey, wheelTiles, demoWords]);

    const designW = frameSize.w / LAYOUT_SCALE;
    const designH = frameSize.h / LAYOUT_SCALE;

    return (
        <Box
            ref={rootRef}
            sx={{
                position: "relative",
                height: "100%",
                minHeight: 0,
                overflow: "hidden",
                pointerEvents: "none",
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                    width: designW,
                    height: designH,
                    transform: `translate(-50%, -50%) scale(${LAYOUT_SCALE})`,
                    transformOrigin: "center center",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <ScreenshotPlayPanel
                    puzzle={puzzle}
                    foundWords={foundWords}
                    clueWord={clueWord}
                    dragIndices={dragIndices}
                    clueMode={clueMode}
                    background={background}
                    theme="dark"
                    fill
                    showBubbles={false}
                    // Clears the phone notch, with a short band of ocean above the grid.
                    topInset={88}
                    compact
                />
            </Box>
            {confettiActive ? (
                <Box
                    sx={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: 10,
                        zIndex: 13,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        px: 1,
                    }}
                >
                    <LandingPhoneStoreCta
                        iconUrl={wordWheelQuestIcon}
                        appName="Word Wheel Quest"
                        playUrl={WORD_WHEEL_QUEST_GOOGLE_PLAY_URL}
                        ariaLabel={t(
                            "landing.word-wheel-google-play-badge-aria",
                            "Get Word Wheel Quest on Google Play (opens in a new tab)",
                        )}
                        tone="dark"
                    />
                </Box>
            ) : null}
            {confettiActive ? (
                <Box
                    aria-hidden
                    sx={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 12,
                        pointerEvents: "none",
                        overflow: "hidden",
                    }}
                >
                    <Confetti
                        width={frameSize.w}
                        height={frameSize.h}
                        recycle={false}
                        numberOfPieces={320}
                        gravity={0.14}
                        tweenDuration={6400}
                        confettiSource={{ x: 0, y: 0, w: frameSize.w, h: 18 }}
                        style={{ pointerEvents: "none" }}
                    />
                </Box>
            ) : null}
        </Box>
    );
}
